import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { map, Observable } from 'rxjs';
import { Customer, CustomerDetails } from '@api/models/customer.model';
import { IndexResponse, ShowResponse } from '@api/models/response.model';
import { AppService } from '../../app.service';

export interface CustomerIndexResponse {
  data: Customer[];
  meta: { current_page: number; from: number; path: string; per_page: number; to: number };
  links: {
    first: string;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
}

@Injectable({
  providedIn: 'root',
})
export class CustomersService {
  private http = inject(HttpClient);
  private appService = inject(AppService);

  constructor() {}

  getCustomers(page: number = 1, filters: Record<string, string> = {}): Observable<IndexResponse<Customer>> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }

    const headers = new HttpHeaders().set('X-Municipality-Id', municipalityId);

    // Convert filters object to query parameters
    const filterParams = Object.entries(filters)
      .map(([key, value]) => `filter[${key}]=${encodeURIComponent(value)}`)
      .join('&');

    const url = `${environment.apiUrl}/customers?page=${page}${filterParams ? '&' + filterParams : ''}`;

    return this.http
      .get<CustomerIndexResponse>(url, { headers })
      .pipe(
        map((response) => {
          return {
            data: response.data,
            meta: {
              current_page: response.meta.current_page,
              from: response.meta.from,
              last_page: 0,
              per_page: response.meta.per_page,
              to: response.meta.to,
              total: 0,
              is_last_page: response.links.next === null,
            },
          };
        })
      );
  }

  getCustomer(id: string): Observable<CustomerDetails> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }

    const headers = new HttpHeaders().set('X-Municipality-Id', municipalityId);

    return this.http
      .get<ShowResponse<CustomerDetails>>(`${environment.apiUrl}/customers/${id}`, {
        headers,
      })
      .pipe(map((response) => response.data));
  }
}
